import {
  LightInstructionsRecord,
  LightInstructionsDTO,
} from '@scale/shared/taxonomyService/dto/AVInstructionsDTO';
import { AVTaxonomyDTO, LegacyTaxonomyDTO } from '@scale/shared/taxonomyService/dto/AVTaxonomyDTO';
import { ProjectInfoDTO } from '@scale/shared/taxonomyService/dto/ProjectInfoDTO';
import {
  SPLIT_TAXONOMY_PRODUCT_TYPES,
  BACKFILL_SUPPORTED_TYPES,
} from '@scale/shared/taxonomyService/dto/constants';
import _ from 'lodash';
import SAlert from 'react-s-alert';
import {
  DRAFT_INSTR_TAG,
  DRAFT_TAX_TAG,
  LATEST_INSTR_TAG,
  LATEST_TAX_TAG,
  SidebarTag,
  splitTaxonomyPropsToTag,
} from 'react-scale/dashboard.scale.com/components/AVTaxonomy/constants';
import { TaskType } from '@scale/shared/defaults';
import { PermissionsStore } from 'react-scale/dashboard.scale.com/components/AVTaxonomy/stores/TaxonomyPermissionsStore';
import { ComposableTaxonomy } from '@scale/shared-types';

// Fast lookup structures that are computeable once API taxonomies, instructions, and the project are loaded
export interface TaxonomyCache {
  isSplitTaxonomy: boolean;
  projectInfo: ProjectInfoDTO;
  latestVersionNumber: number;
  projectHasNoTaxonomies: boolean;
  numLegacyTaxonomies: number;

  allTaxonomies: AVTaxonomyDTO[];
  sortedRootTaxonomies: AVTaxonomyDTO[];
  taxIdToTaxonomy: Record<string, AVTaxonomyDTO>;
  taxIdToSortedLightInstructions: LightInstructionsRecord;
  taxIdToTag: Record<string, SidebarTag>;
  taxIdToActionPerms: Record<string, ActionPerms>;
  rootTaxIdToSortedSubtaxonomies: Record<string, AVTaxonomyDTO[]>;
  subtaxIdToDepth: Record<string, number>;

  instructionsIdToInstructions: Record<string, LightInstructionsDTO>;
  instructionsIdToTag: Record<string, SidebarTag>;
  instructionsIdToActionPerms: Record<string, InstructionsActionPerms>;
  masterTaxonomy?: ComposableTaxonomy;
  hasMasterInstructions?: boolean;
}

interface ActionPerms {
  canEdit?: boolean;
  canPublish?: boolean;
  canBackfillTasks?: boolean;
  canInheritInstructions?: boolean;
  canCreateInstructions?: boolean;
  // Only false for root SFU taxonomies
  canDiff?: boolean;

  // SFU mode actions
  canCreateSubtaxonomy?: boolean;
  canViewSrnHistory?: boolean;
  canInherit?: boolean;
  canFork?: boolean;
  canCommit?: boolean;
  canRecall?: boolean;
  canCommitMultiple?: boolean;
}

interface InstructionsActionPerms {
  canEdit?: boolean;
  canPublish?: boolean;
  canBackfillTasks?: boolean;
}

export function makeTaxonomyCache(
  taxonomies: AVTaxonomyDTO[],
  legacyTaxonomies: LegacyTaxonomyDTO[],
  taxIdToSortedLightInstructions: LightInstructionsRecord,
  projectInfo: ProjectInfoDTO,
  permissions: PermissionsStore,
  masterTaxonomy?: AVTaxonomyDTO | null,
  hasMasterInstructions?: boolean,
): TaxonomyCache {
  const { isCorp, isEditable: userCanEdit, customerCanPublish } = permissions;

  const isSplitTaxonomy =
    SPLIT_TAXONOMY_PRODUCT_TYPES.includes(projectInfo.taskType) &&
    projectInfo.taskType === TaskType.MultiStage;
  const allTaxonomies = taxonomies;
  const taxIdToTaxonomy = _.keyBy(taxonomies, tax => tax._id);
  const rootTaxonomies = taxonomies.filter(tax => !tax.parentTaxonomyId);
  const sortedRootTaxonomies = _.orderBy(rootTaxonomies, tax => tax.version, ['desc']);
  const projectHasNoTaxonomies = sortedRootTaxonomies.length === 0;
  const latestVersionNumber = sortedRootTaxonomies[0]?.version ?? 0;
  // negative 1 is okay here cuz this variable is just used for matching
  const latestPublishedVersionNumber = latestVersionNumber - 1;

  const baseCache = {
    isSplitTaxonomy,
    projectInfo,
    allTaxonomies,
    taxIdToTaxonomy,
    sortedRootTaxonomies,
    projectHasNoTaxonomies,
    latestVersionNumber,
    numLegacyTaxonomies: legacyTaxonomies.length,
    masterTaxonomy: masterTaxonomy?.taxonomy,
    hasMasterInstructions,
  };

  const taxIdToTag: Record<string, SidebarTag> = {};
  const taxIdToActionPerms: Record<string, ActionPerms> = {};
  const rootTaxIdToSortedSubtaxonomies: Record<string, AVTaxonomyDTO[]> = {};
  const subtaxIdToDepth: Record<string, number> = {};
  const instructionsIdToInstructions: Record<string, LightInstructionsDTO> = {};
  const instructionsIdToTag: Record<string, SidebarTag> = {};
  const instructionsIdToActionPerms: Record<string, InstructionsActionPerms> = {};

  if (projectHasNoTaxonomies) {
    return {
      ...baseCache,
      taxIdToTaxonomy,
      taxIdToSortedLightInstructions,
      taxIdToTag,
      taxIdToActionPerms,
      rootTaxIdToSortedSubtaxonomies,
      subtaxIdToDepth,
      instructionsIdToInstructions,
      instructionsIdToTag,
      instructionsIdToActionPerms,
    };
  }

  Object.values(taxIdToSortedLightInstructions).forEach(instructionsArray => {
    instructionsArray.forEach(instructions => {
      const { _id: instructionsId, isPublished } = instructions;
      instructionsIdToInstructions[instructionsId] = instructions;

      if (isPublished) {
        instructionsIdToActionPerms[instructionsId] = {
          canBackfillTasks:
            isCorp && BACKFILL_SUPPORTED_TYPES.includes(projectInfo.taskType) && isPublished,
        };
      } else {
        instructionsIdToActionPerms[instructionsId] = {
          canEdit: isCorp && userCanEdit,
          canPublish: isCorp && userCanEdit,
        };
      }
    });

    const latestInstruction = instructionsArray[0];
    if (latestInstruction) {
      if (!latestInstruction.isPublished) {
        instructionsIdToTag[latestInstruction._id] = DRAFT_INSTR_TAG;
      } else {
        SAlert.error(
          `The most recent instructions object ${latestInstruction._id} is not in a draft state and is probably corrupted. This page may not work as intended`,
        );
      }
    }

    const secondLatestInstruction = instructionsArray[1];
    if (secondLatestInstruction) {
      if (secondLatestInstruction.isPublished) {
        instructionsIdToTag[secondLatestInstruction._id] = LATEST_INSTR_TAG;
      } else {
        SAlert.error(
          `The second most recent instructions object ${secondLatestInstruction._id} is not in a published state and is probably corrupted. This page may not work as intended`,
        );
      }
    }
  });

  // start non-SFU logic
  if (!isSplitTaxonomy) {
    const latestRootTaxonomy: AVTaxonomyDTO | undefined = sortedRootTaxonomies[0];
    const secondLatestRootTaxonomy: AVTaxonomyDTO | undefined = sortedRootTaxonomies[1];

    if (latestRootTaxonomy) {
      if (!latestRootTaxonomy.isCustomerPublished) {
        taxIdToTag[latestRootTaxonomy._id] = DRAFT_TAX_TAG;
      } else {
        SAlert.error(
          `The most recent Tax ${latestRootTaxonomy._id} is not in a draft state and is probably corrupted. This page may not work as intended`,
        );
      }
    }

    if (secondLatestRootTaxonomy) {
      if (secondLatestRootTaxonomy.isCustomerPublished) {
        taxIdToTag[secondLatestRootTaxonomy._id] = LATEST_TAX_TAG;
      } else {
        SAlert.error(
          `The second most recent Tax ${secondLatestRootTaxonomy._id} is not in a published state and is probably corrupted. This page may not work as intended`,
        );
      }
    }

    sortedRootTaxonomies.forEach(
      ({ _id, isCustomerPublished, version, taxonomy: taxonomyBody }) => {
        if (isCustomerPublished) {
          taxIdToActionPerms[_id] = {
            canDiff: true,
            canCreateInstructions:
              isCorp &&
              version <= latestPublishedVersionNumber &&
              !taxIdToSortedLightInstructions[_id]?.length,
            canBackfillTasks: isCorp && BACKFILL_SUPPORTED_TYPES.includes(projectInfo.taskType),
          };
        } else {
          const hasInstructionsUnderPrevTaxVersion = Boolean(
            secondLatestRootTaxonomy &&
              taxIdToSortedLightInstructions[secondLatestRootTaxonomy._id]?.length,
          );

          taxIdToActionPerms[_id] = {
            canDiff: true,
            canEdit: userCanEdit,
            canPublish: (isCorp || customerCanPublish) && userCanEdit && !_.isEmpty(taxonomyBody),
            canInheritInstructions:
              (isCorp || customerCanPublish) &&
              !taxIdToSortedLightInstructions[_id]?.length &&
              hasInstructionsUnderPrevTaxVersion,
          };
        }
      },
    );

    return {
      ...baseCache,
      taxIdToTaxonomy,
      taxIdToSortedLightInstructions,
      taxIdToTag,
      taxIdToActionPerms,
      rootTaxIdToSortedSubtaxonomies,
      subtaxIdToDepth,
      instructionsIdToInstructions,
      instructionsIdToTag,
      instructionsIdToActionPerms,
    };
  }

  // start SFU logic
  // TODO: implement `customerCanPublish` in SFU
  sortedRootTaxonomies.forEach(rootTax => {
    const subtaxonomies = taxonomies.filter(
      tax => !!tax.parentTaxonomyId && tax.version === rootTax.version,
    );

    const orderedSubtaxonomies = getSubtaxonomyOrderingAndUpdateRecords(
      rootTax,
      subtaxonomies,
      taxIdToTag,
      subtaxIdToDepth,
    );
    rootTaxIdToSortedSubtaxonomies[rootTax._id] = orderedSubtaxonomies;

    const isAtLeastOneSubtaxonomyCommitted = subtaxonomies.some(tax => tax.isCommitted);
    taxIdToTag[rootTax._id] = splitTaxonomyPropsToTag({
      ...rootTax,
      isCommitted: isAtLeastOneSubtaxonomyCommitted,
      isRoot: true,
    });

    taxIdToActionPerms[rootTax._id] = {
      canCreateSubtaxonomy: userCanEdit && !rootTax.isCustomerPublished,
      canInherit: userCanEdit && !rootTax.isCustomerPublished && !rootTax.isCommitted,
      canPublish: userCanEdit && rootTax.version === latestVersionNumber,
      canCreateInstructions: false,
    };

    // eslint-disable-next-line complexity
    orderedSubtaxonomies.forEach(subtax => {
      taxIdToActionPerms[subtax._id] = {
        canCreateInstructions: isCorp && subtax.isCustomerPublished,
        canDiff: true,
        canEdit:
          userCanEdit &&
          !subtax.isCommitted &&
          // Ops can substage taxonomies
          ((subtax.isInternal && isCorp) ||
            // Customer can only edit draft stage taxonomies
            (!subtax.isCustomerPublished && !subtax.isInternal)),
        canInherit:
          isCorp &&
          userCanEdit &&
          subtax.isCustomerPublished &&
          !subtax.isCommitted &&
          !subtax.isInternal,
        canFork:
          isCorp &&
          userCanEdit &&
          subtax.isCustomerPublished &&
          !subtax.isCommitted &&
          !subtax.isInternal,
        canCommit:
          isCorp &&
          userCanEdit &&
          (subtax.isInternal || subtax.isCustomerPublished) &&
          !subtax.isCommitted,
        canRecall: isCorp && userCanEdit && subtax.isCommitted,
        canViewSrnHistory: isCorp && !!subtax.srnHistory?.length,
      };
    });

    taxIdToActionPerms[rootTax._id].canCommitMultiple = orderedSubtaxonomies.every(
      subtax => taxIdToActionPerms[subtax._id]?.canCommit,
    );
  });

  return {
    ...baseCache,
    taxIdToTaxonomy,
    taxIdToSortedLightInstructions,
    taxIdToTag,
    taxIdToActionPerms,
    rootTaxIdToSortedSubtaxonomies,
    subtaxIdToDepth,
    instructionsIdToInstructions,
    instructionsIdToTag,
    instructionsIdToActionPerms,
  };
}

/*
We want this sidebar ordering:

- root tax
  - customer tax
    - ops fork of customer tax
  - next customer tax
    - ops fork of customer tax
  - ops forks of root tax / ops forks that have no parent
*/
function getSubtaxonomyOrderingAndUpdateRecords(
  rootTaxonomy: AVTaxonomyDTO,
  subtaxonomies: AVTaxonomyDTO[],
  taxIdToTag: Record<string, SidebarTag>,
  taxIdToDepth: Record<string, number>,
): AVTaxonomyDTO[] {
  const orderedSubTaxonomies: AVTaxonomyDTO[] = [];

  const taxIdToChildTaxonomies = _.groupBy(subtaxonomies, tax => tax.parentTaxonomyId);

  const [substageTaxonomies, stageTaxonomies] = _.partition(
    subtaxonomies.filter(tax => tax._id !== rootTaxonomy._id),
    tax => tax.isInternal,
  );

  const idToSubstageTaxonomies = _.keyBy(substageTaxonomies, '_id');
  const substageTaxonomyIds = new Set(substageTaxonomies.map(tax => tax._id));

  stageTaxonomies.forEach(stageTax => {
    orderedSubTaxonomies.push(stageTax);
    taxIdToTag[stageTax._id] = splitTaxonomyPropsToTag(stageTax);
    taxIdToDepth[stageTax._id] = 1;

    const childTaxonomies = taxIdToChildTaxonomies[stageTax._id];
    if (childTaxonomies) {
      childTaxonomies.forEach(substageTax => {
        orderedSubTaxonomies.push(substageTax);
        taxIdToTag[substageTax._id] = splitTaxonomyPropsToTag(substageTax);
        taxIdToDepth[substageTax._id] = 2;

        substageTaxonomyIds.delete(substageTax._id);
      });
    }
  });

  // Get the ops subtaxonomies that are unexpected: either have parent as root or no parentTaxonomyId
  Array.from(substageTaxonomyIds).forEach(taxId => {
    const substageTaxonomy = idToSubstageTaxonomies[taxId];

    orderedSubTaxonomies.push(substageTaxonomy);
    taxIdToTag[substageTaxonomy._id] = splitTaxonomyPropsToTag(substageTaxonomy);
    taxIdToDepth[substageTaxonomy._id] = 1;
  });

  return orderedSubTaxonomies;
}
